import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import TurnBlock from '../components/debate/TurnBlock';
import VoteBar from '../components/debate/VoteBar';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Debate, Turn } from '../types';

const DebateView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [debate, setDebate] = useState<Debate | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [activeTab, setActiveTab] = useState<'transcript' | 'votes'>('transcript');
  const [loading, setLoading] = useState(true);
  const [hasVoted, setHasVoted] = useState(false);
  const [userVote, setUserVote] = useState<'pro' | 'con' | null>(null);
  
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef<boolean>(true);
  
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  useEffect(() => {
    const fetchDebate = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Fetch debate details with profile info using the view
        const { data: debateData, error: debateError } = await supabase
          .from('debates_with_profiles')
          .select('*')
          .eq('id', id)
          .single();
        
        if (debateError) {
          console.error('Error fetching debate:', debateError);
          if (isMountedRef.current) {
            setDebate(null);
          }
          return;
        }
        
        if (debateData) {
          // Map status values
          let status: 'live' | 'upcoming' | 'finished' = 'finished';
          if (debateData.status === 'active') status = 'live';
          else if (debateData.status === 'pending') status = 'upcoming';
          else if (debateData.status === 'completed') status = 'finished';
          
          // Transform to Debate interface
          const transformedDebate: Debate = {
            id: debateData.id,
            topic: debateData.topic,
            description: debateData.summary_overall || '',
            status: status,
            sideA: {
              position: debateData.pro_agent_name || 'Supporting',
              color: '#3A68A0'
            },
            sideB: {
              position: debateData.con_agent_name || 'Opposing',
              color: '#E5442D'
            },
            voteCount: debateData.total_votes || 0,
            totalRounds: 3,
            currentRound: undefined,
            tags: [],
            createdAt: new Date(debateData.created_at),
            startedAt: debateData.created_at ? new Date(debateData.created_at) : undefined,
            finishedAt: debateData.completed_at ? new Date(debateData.completed_at) : undefined,
            createdBy: debateData.username || debateData.display_name || 'Anonymous',
            proVotes: debateData.pro_votes || 0,
            conVotes: debateData.con_votes || 0
          };
          
          if (isMountedRef.current) {
            setDebate(transformedDebate);
          }
          
          // Fetch turns/arguments from debate_turns table
          const { data: turnsData, error: turnsError } = await supabase
            .from('debate_turns')
            .select('*')
            .eq('debate_id', id)
            .order('turn_number', { ascending: true })
            .order('created_at', { ascending: true });
          
          if (turnsError) {
            console.error('Error fetching turns:', turnsError);
          } else if (turnsData && isMountedRef.current) {
            // Transform to Turn interface
            const transformedTurns: Turn[] = turnsData.map((turn: any) => ({
              id: turn.id,
              debateId: turn.debate_id,
              side: turn.agent_side === 'pro' ? 'A' as const : 'B' as const,
              round: turn.turn_number,
              type: turn.phase === 'research' ? 'research' :
                    turn.phase === 'opening' ? 'opening' : 
                    turn.phase === 'rebuttal' ? 'rebuttal' : 'closing',
              content: turn.content,
              timestamp: new Date(turn.created_at)
            }));
            
            setTurns(transformedTurns);
          }
        }
      } catch (error) {
        console.error('Error in fetchDebate:', error);
        if (isMountedRef.current) {
          setDebate(null);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };
    
    fetchDebate();
  }, [id]);
  
  // Check if user has already voted
  useEffect(() => {
    const checkUserVote = async () => {
      if (!id || !user) return;
      
      try {
        const { data } = await supabase
          .from('votes')
          .select('vote_for')
          .eq('debate_id', id)
          .eq('user_id', user.id)
          .single();
        
        if (data) {
          setHasVoted(true);
          setUserVote(data.vote_for);
        }
      } catch {
        // No vote found is ok
        console.log('No existing vote found');
      }
    };
    
    checkUserVote();
  }, [id, user]);
  
  const handleVote = async (side: 'A' | 'B', metrics?: { logic: number; clarity: number; evidence: number }) => {
    if (!user) {
      // Redirect to sign in if not authenticated
      navigate('/signin', { state: { from: `/debate/${id}` } });
      return;
    }
    
    if (!id) return;
    
    try {
      const voteFor = side === 'A' ? 'pro' : 'con';
      // Optimistic UI update
      setHasVoted(true);
      setUserVote(voteFor);
      
      // Insert or update vote
      const { error } = await supabase
        .from('votes')
        .upsert({
          debate_id: id,
          user_id: user.id,
          vote_for: voteFor
        }, {
          onConflict: 'debate_id,user_id'
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Refetch debate to get updated vote counts (best effort)
      const { data: updatedDebate } = await supabase
        .from('debates_with_profiles')
        .select('*')
        .eq('id', id)
        .single();
      
      if (updatedDebate) {
        setDebate(prev => prev ? {
          ...prev,
          voteCount: updatedDebate.total_votes || 0,
          proVotes: updatedDebate.pro_votes || 0,
          conVotes: updatedDebate.con_votes || 0
        } : null);
      }
      
      console.log('Vote submitted successfully:', { side, voteFor });
    } catch (error) {
      console.error('Error submitting vote:', error);
      alert('Failed to submit vote. Please try again.');
    }
  };
  
  if (loading) {
    return (
      <div className="blueprint-grid flex min-h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center">
          <div className="relative h-8 w-8">
            <div className="absolute inset-0 rounded-full border-2 border-hairline" />
            <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-charcoal" />
          </div>
          <p className="mt-5 text-sm text-ink-muted">Loading debate…</p>
        </div>
      </div>
    );
  }
  
  if (!debate) {
    return (
      <div className="blueprint-grid flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <p className="font-serif text-2xl text-charcoal">Debate not found</p>
          <button
            onClick={() => navigate('/explore')}
            className="link-ink mt-4 text-sm font-medium"
          >
            Back to Explore
          </button>
        </div>
      </div>
    );
  }
  
  const sideATurns = turns.filter(t => t.side === 'A');
  const sideBTurns = turns.filter(t => t.side === 'B');
  const proShare = debate.voteCount > 0 ? Math.round(((debate.proVotes || 0) / debate.voteCount) * 100) : 50;
  
  return (
    <div className="relative isolate min-h-screen bg-white">
      <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

      {/* Header Bar */}
      <div className="relative z-10 border-b border-black/[0.08] bg-white/85 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0">
              <span className="eyebrow">Public debate</span>
              <h1 className="mt-2 text-balance font-serif text-2xl tracking-tight text-charcoal sm:text-3xl">
                {debate.topic}
              </h1>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-ink-muted">
                {debate.createdBy && <>by {debate.createdBy} · </>}
                {debate.status === 'live' ? 'Live now' : debate.status}
                {debate.voteCount > 0 && <> · {debate.voteCount.toLocaleString()} votes</>}
              </p>
            </div>
            {debate.voteCount > 0 && (
              <div className="w-full max-w-[220px] flex-shrink-0">
                <div className="flex h-1.5 overflow-hidden rounded-full bg-hairline">
                  <div className="bg-steel-blue" style={{ width: `${proShare}%` }} />
                  <span className="w-[2px] bg-white" />
                  <div className="flex-1 bg-vermilion" />
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="font-mono text-[10px] font-medium text-steel-blue">{proShare}% for</span>
                  <span className="font-mono text-[10px] font-medium text-vermilion">{100 - proShare}% against</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Progress Bar */}
          {debate.status === 'live' && debate.currentRound && debate.totalRounds && (
            <div className="mt-4">
              <div className="h-1 w-full overflow-hidden rounded-full bg-hairline">
                <motion.div
                  className="h-1 rounded-full bg-charcoal"
                  initial={{ width: 0 }}
                  animate={{ width: `${(debate.currentRound / debate.totalRounds) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          )}
          
          {/* Tab Navigation */}
          <div className="mt-5 flex gap-1 rounded-full border border-black/[0.07] bg-white/80 p-1 backdrop-blur-sm sm:w-fit">
            {(['transcript', 'votes'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors sm:flex-none ${
                  activeTab === tab
                    ? 'bg-charcoal text-white'
                    : 'text-ink-muted hover:text-charcoal'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="relative z-10 mx-auto px-4 py-8 sm:px-6 lg:px-8" style={{ maxWidth: '1400px' }}>
        {activeTab === 'transcript' && (
          <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
            {/* Side A Column — For */}
            <div className="w-full">
              <div className="mb-4 flex items-center gap-2 xl:sticky xl:top-20 xl:z-20">
                <span className="h-3 w-1 rounded-full bg-steel-blue" />
                <span className="font-mono text-xs font-medium uppercase tracking-wider text-steel-blue">
                  For · {debate.sideA.position}
                </span>
              </div>
              <div className="space-y-6">
                {sideATurns.length === 0 ? (
                  <div className="card-frame p-12 text-center">
                    <div className="animate-pulse space-y-3">
                      <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-steel-blue/10" />
                      <p className="text-base text-ink-muted">Preparing opening statement…</p>
                      <p className="text-sm text-ink-muted/70">The debate will begin shortly</p>
                    </div>
                  </div>
                ) : (
                  sideATurns.map(turn => (
                    <TurnBlock key={turn.id} turn={turn} side="A" />
                  ))
                )}
              </div>
            </div>
            
            {/* Side B Column — Against */}
            <div className="w-full">
              <div className="mb-4 flex items-center gap-2 xl:sticky xl:top-20 xl:z-20">
                <span className="h-3 w-1 rounded-full bg-vermilion" />
                <span className="font-mono text-xs font-medium uppercase tracking-wider text-vermilion">
                  Against · {debate.sideB.position}
                </span>
              </div>
              <div className="space-y-6">
                {sideBTurns.length === 0 ? (
                  <div className="card-frame p-12 text-center">
                    <div className="animate-pulse space-y-3">
                      <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-vermilion/10" />
                      <p className="text-base text-ink-muted">Preparing opening statement…</p>
                      <p className="text-sm text-ink-muted/70">The debate will begin shortly</p>
                    </div>
                  </div>
                ) : (
                  sideBTurns.map(turn => (
                    <TurnBlock key={turn.id} turn={turn} side="B" />
                  ))
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'votes' && (
          <div className="mx-auto max-w-3xl">
            <div className="card-frame p-7">
              <span className="eyebrow">Results</span>
              <h3 className="mt-2 font-serif text-2xl text-charcoal">Vote breakdown</h3>
              {debate.status === 'finished' ? (
                <div className="mt-6 space-y-6">
                  <div>
                    <div className="mb-2 flex items-baseline justify-between">
                      <span className="font-mono text-xs font-medium uppercase tracking-wider text-steel-blue">
                        For · {debate.sideA.position}
                      </span>
                      <span className="font-serif text-2xl text-charcoal">{proShare}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-hairline">
                      <motion.div
                        className="h-2 rounded-full bg-steel-blue"
                        initial={{ width: 0 }}
                        animate={{ width: `${proShare}%` }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 flex items-baseline justify-between">
                      <span className="font-mono text-xs font-medium uppercase tracking-wider text-vermilion">
                        Against · {debate.sideB.position}
                      </span>
                      <span className="font-serif text-2xl text-charcoal">{100 - proShare}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-hairline">
                      <motion.div
                        className="h-2 rounded-full bg-vermilion"
                        initial={{ width: 0 }}
                        animate={{ width: `${100 - proShare}%` }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                      />
                    </div>
                  </div>
                  <p className="border-t border-hairline pt-4 text-center font-mono text-[11px] uppercase tracking-wider text-ink-muted">
                    {debate.voteCount.toLocaleString()} total votes
                  </p>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-ink-muted">
                  Voting results will be available when the debate ends
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Vote Bar - Only show on transcript tab */}
      {debate.status !== 'upcoming' && activeTab === 'transcript' && (
        <VoteBar
          debateId={debate.id}
          onVote={handleVote}
          disabled={false}
          hasVoted={hasVoted}
          userVote={userVote}
          isAuthenticated={!!user}
        />
      )}
    </div>
  );
};

export default DebateView;
